import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { PageHelmet } from '@/config/helmet';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import GalleryProductCard from '@/components/GalleryProductCard';
import {
  CATEGORY_ORDER,
  getAllGalleryProducts,
  getGalleryProductsForCategory,
} from '@/data/galleryProducts';

const categoryNames = {
  garniture: 'Garniture',
  stolice: 'Stolice',
  loze: 'Lože',
  kreveti: 'Kreveti',
  sve: 'Svi proizvodi',
};

const FILTER_CHIPS = [
  { key: null, label: 'Sve' },
  { key: 'garniture', label: 'Garniture' },
  { key: 'stolice', label: 'Stolice' },
  { key: 'loze', label: 'Lože' },
  { key: 'kreveti', label: 'Kreveti' },
];

const FALLBACK_BLUR =
  'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?q=80&w=800';

/** Fisher–Yates: novi niz u slučajnom redosledu (za „Svi proizvodi“). */
function shuffleArray(items) {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ProductsPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [manifest, setManifest] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const filterKey =
    category === 'sve' && CATEGORY_ORDER.includes(searchParams.get('kat'))
      ? searchParams.get('kat')
      : null;

  const setFilterKey = (key) => {
    if (!key) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ kat: key }, { replace: true });
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    const url = `${base}/gallery/manifest.json`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('manifest');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setManifest(data && typeof data === 'object' && !Array.isArray(data) ? data : {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setManifest({});
          setLoadError(
            'Nije učitan katalog slika. Pokreni `npm run gallery:manifest` ili `npm start`.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const baseProducts = useMemo(() => {
    if (!manifest) return [];
    if (category === 'sve') {
      return shuffleArray(getAllGalleryProducts(manifest));
    }
    return getGalleryProductsForCategory(manifest, category);
  }, [manifest, category]);

  const products = useMemo(() => {
    if (category !== 'sve' || !filterKey) {
      return baseProducts;
    }
    return baseProducts.filter((p) => p.id.startsWith(`${filterKey}-`));
  }, [baseProducts, category, filterKey]);

  const categoryName = categoryNames[category] || 'Proizvodi';
  const helmetPage = ['garniture', 'stolice', 'loze', 'kreveti', 'sve'].includes(category)
    ? category
    : 'home';

  const heroBlurSrc = products[0]?.image || baseProducts[0]?.image || FALLBACK_BLUR;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category, filterKey]);

  return (
    <div className="min-h-screen">
      <PageHelmet page={helmetPage} />
      <Navbar />

      <section className="relative pt-32 pb-16 overflow-hidden bg-gradient-to-br from-secondary/40 via-background to-secondary/30">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 right-10 w-96 h-96 opacity-10">
            <img
              src={heroBlurSrc}
              alt=""
              className="w-full h-full object-cover rounded-3xl"
              style={{ filter: 'blur(20px)' }}
            />
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Button
            variant="outline"
            size="sm"
            className="mb-6 rounded-full h-9 text-sm font-normal border-border/60 hover:bg-primary/5 hover:border-primary/25 touch-manipulation"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/');
              setTimeout(() => {
                document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
              }, 200);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/');
              setTimeout(() => {
                document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
              }, 200);
            }}
          >
            <LayoutGrid className="mr-2 w-4 h-4" />
            Nazad na proizvode
          </Button>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
            {categoryName}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            {category === 'sve'
              ? 'Kompletan katalog: garniture, stolice, lože i kreveti. Filtrirajte po kategoriji.'
              : 'Pronađite savršen komad nameštaja za vaš prostor'}
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 relative bg-gallery-card">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {!manifest && (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">Učitavanje kataloga…</p>
            </div>
          )}

          {manifest && loadError && (
            <p className="text-center text-sm text-destructive mb-8 max-w-xl mx-auto">{loadError}</p>
          )}

          {manifest && category === 'sve' && !loadError && baseProducts.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8 max-w-2xl mx-auto">
              {FILTER_CHIPS.map(({ key, label }) => {
                const active = (key === null && filterKey === null) || key === filterKey;
                return (
                  <Button
                    key={label}
                    type="button"
                    variant={active ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full h-9 text-sm font-normal"
                    onClick={() => setFilterKey(key)}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          )}

          {manifest && !loadError && products.length === 0 && (
            <div className="text-center py-20 lg:py-28">
              {category === 'sve' && filterKey ? (
                <p className="text-xl text-muted-foreground max-w-lg mx-auto">
                  Nema proizvoda u ovom filteru. Izaberite „Sve“ ili drugu kategoriju.
                </p>
              ) : category === 'sve' ? (
                <p className="text-xl text-muted-foreground max-w-lg mx-auto">
                  Katalog je prazan. Dodaj slike u{' '}
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">public/gallery/</code> po
                  kategorijama i pokreni{' '}
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">npm run gallery:manifest</code>.
                </p>
              ) : (
                <p className="text-xl text-muted-foreground max-w-lg mx-auto">
                  U ovoj kategoriji još nema fotografija. Dodaj slike u{' '}
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                    public/gallery/{category}/
                  </code>{' '}
                  pa ponovo pokreni dev server ili{' '}
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">npm run gallery:manifest</code>.
                </p>
              )}
            </div>
          )}

          {manifest && products.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8">
              {products.map((product) => (
                <GalleryProductCard
                  key={product.id}
                  product={product}
                  routeCategory={category === 'sve' ? undefined : category}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
